import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { alert, confirm, rovingMenuItem } from '@orgazone/ui-web';
import { currencyFor, setCurrentCurrency } from '@orgazone/money-format';
import { setCurrentVocabulary } from './vocabulary';
import { useAuth, useCrossTenant } from './auth';
import { api, type StoreContextView } from './api';
import { t } from './i18n';

/**
 * Pose la devise du magasin actif sur le formatage de TOUTE la SPA.
 * ⚠️ L'ALLURE (symbole, ponctuation) VIENT DE `money-format`, la même table que le bon de commande imprimé au serveur :
 *    l'écran et le document envoyé au fournisseur écrivent un montant de la même façon.
 */
function appliquerDevise(c: { code: string; fraction: number } | null): void {
  setCurrentCurrency(currencyFor(c?.code ?? 'EUR', c?.fraction ?? 2));
}

/**
 * LE CONTEXTE MAGASIN — lu UNE fois, partagé par le bandeau, le sélecteur et les écrans.
 *
 * UTILITÉ : le back-office administre UN magasin, et le magasin actif est un contexte, jamais un filtre
 * (CADRAGE §1 et §5). En changer recharge une autre base. On ne doit jamais avoir à deviner sur quel
 * magasin on agit, ni découvrir après coup qu'on s'est trompé de base.
 *
 * ⚠️ UN SEUL APPEL POUR TOUT LE MONDE. Trois lectures concurrentes (magasins ouverts, cross-tenant,
 * magasin actif) multiplient les instants où le bandeau affiche un contexte incomplet — et un bandeau
 * qui hésite sur le magasin est pire qu'absent. Les écrans lisent le même état : ils ne peuvent donc pas
 * afficher les articles d'un magasin sous le nom d'un autre.
 *
 * ⚠️ LE MAGASIN ACTIF VIENT DU SERVEUR, jamais d'un état local. Ce qui décide quelle BASE on lit ne peut
 * pas être une valeur que le navigateur choisit : un contexte déduit d'une URL ou d'un `localStorage`
 * s'effacerait d'un coup d'éditeur d'adresse pendant que l'accès resterait ouvert (§23.0).
 */
interface StoreScopeCtx {
  loading: boolean;
  /** Motif RÉEL d'un échec de lecture — jamais transformé en « aucun magasin ». */
  error: string | null;
  view: StoreContextView | null;
  /** Change de magasin : confirme, écrit côté serveur, puis RECHARGE. */
  choose: (gln: string) => Promise<void>;
}

const Ctx = createContext<StoreScopeCtx | null>(null);

export function StoreScopeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<StoreContextView | null>(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let vivant = true;
    setLoading(true);
    api.storeContext()
      .then((v) => {
        if (!vivant) return;
        // ⚠️ LA DEVISE SE POSE AVANT LE PREMIER RENDU D'ÉCRAN, et c'est tout l'intérêt de la porter
        // dans le contexte : la poser après ferait afficher une première page en euros avant de se
        // corriger, et personne ne saurait laquelle des deux valeurs il a lue (§23.0). Le changement
        // de magasin, lui, recharge la page entière — la devise ne peut donc pas rester périmée.
        appliquerDevise(v.active?.currency ?? null);
        // ⚠️ LES MOTS SE POSENT AU MÊME INSTANT QUE LA DEVISE, et avant `setView` : un écran qui
        // rendrait « Saison » puis « Millésime » pour le même champ trahirait le même défaut (§23.0).
        setCurrentVocabulary(v.active?.vocabulary ?? null);
        setView(v); setError(null);
      })
      .catch((e: unknown) => { if (vivant) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (vivant) setLoading(false); });
    return () => { vivant = false; };
  }, [user]);

  /**
   * CHANGER DE MAGASIN = CONFIRMATION PUIS RECHARGEMENT PLEINE PAGE.
   *
   * ⚠️ Le rechargement n'est pas une facilité : « on ne mélange jamais deux magasins dans une vue »
   * (CADRAGE §5). Tout état React survivant — page courante, filtres, lignes déjà chargées — ferait
   * afficher les articles du magasin A sous le bandeau du magasin B. Une navigation douce rouvrirait
   * exactement ce risque.
   * ⚠️ Un échec passe par le canal ALERTE avec son motif réel, jamais par un toast : une erreur qui
   * s'efface seule est une erreur perdue (§24.1quater), et l'écran resterait celui d'un succès.
   */
  const choose = useCallback(async (gln: string) => {
    const target = view?.stores.find((s) => s.gln === gln);
    if (!target || target.gln === view?.active?.gln) return;
    // Le texte de la maquette (modale « Passer sur X ? »), avec le GLN — c'est lui qui désigne la base.
    const ok = await confirm({
      title: t('Passer sur {name} ?', { name: target.name }),
      message: t('Chaque magasin possède sa propre base : le back-office va se recharger entièrement sur « {name} » (GLN {gln}). Votre travail en cours sur le magasin actuel n’est pas emporté.', { name: target.name, gln: target.gln }),
      confirmLabel: t('Ouvrir {name}', { name: target.name }),
    });
    if (!ok) return;
    try {
      await api.chooseStore(gln);
    } catch (e) {
      await alert({
        title: t('Magasin non ouvert'),
        message: t('Le back-office n’a pas pu ouvrir « {name} » : {raison}', { name: target.name, raison: e instanceof Error ? e.message : String(e) }),
      });
      return;
    }
    // ⚠️ Rechargement seulement APRÈS l'écriture réussie : recharger d'abord ferait revenir l'écran sur
    // l'ancien magasin sans que rien n'explique pourquoi.
    window.location.reload();
  }, [view]);

  const value = useMemo<StoreScopeCtx>(() => ({ loading, error, view, choose }), [loading, error, view, choose]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Le contexte magasin de la session — pour le bandeau, le sélecteur et les écrans qui lisent une base. */
export function useStoreScope(): StoreScopeCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStoreScope hors StoreScopeProvider');
  return v;
}

/**
 * LE BANDEAU PERMANENT de la barre du haut — il dit sur quel MAGASIN on agit.
 *
 * ⚠️ MONTÉ DANS LA TOPBAR (`position: sticky`), donc présent sur TOUS les écrans et jamais emporté par
 * le défilement. Un bandeau de page sortirait de l'écran au premier mouvement — et « on ne doit jamais
 * oublier où l'on est ».
 *
 * ⚠️ « À LA PLACE DE QUI » N'EST PLUS ICI : c'est la BANDE NOIRE pleine largeur (`StaffStrip`), au-dessus
 * de la barre du haut, comme la maquette. Glissé dans la topbar, l'avertissement devenait un élément
 * d'interface parmi d'autres, à la même échelle que le nom du magasin — alors qu'il dit qu'on agit dans
 * la base de quelqu'un d'autre.
 */
export function StoreContextBanner() {
  const cross = useCrossTenant();
  const { loading, error, view, choose } = useStoreScope();

  if (cross.loading || loading) return <Row tone="neutre" etiquette={t('Contexte')} texte={t('Lecture du contexte de cette session…')} />;

  // Le cross-tenant est annoncé par la bande noire ; ici il porte la MÊME capsule que tout le monde,
  // chevron désactivé (la session est bornée à un magasin, l'API refuse d'en changer).
  if (cross.ctx?.active) {
    return (
      <CapsuleMagasin
        nom={cross.ctx.storeName} meta={t('GLN {gln}', { gln: cross.ctx.gln })}
        stores={[]} activeGln={cross.ctx.gln} onChoose={() => Promise.resolve()}
        motifDesactive={t('Session bornée à ce magasin — le changement passe par la Console.')}
      />
    );
  }

  // ⚠️ Un échec se DIT avec son motif réel : sans cela, une panne de lecture serait indiscernable d'un
  // compte sans aucun droit — l'un se signale, l'autre se demande à un administrateur.
  if (error || !view) {
    return <Row tone="alerte" etiquette={t('Contexte illisible')} texte={t('Le magasin de cette session n’a pas pu être lu : {v}', { v: error ?? t('réponse vide du serveur') })} />;
  }

  if (view.stores.length === 0) {
    return (
      <Row
        tone="alerte"
        etiquette={t('Aucun magasin')}
        texte={t('Aucun magasin n’est ouvert à ce compte. Rien n’est ouvert implicitement : un administrateur doit accorder l’accès magasin par magasin.')}
      />
    );
  }

  // ⚠️ Le GLN vit dans la ligne méta : deux magasins d'une même enseigne portent souvent le même nom
  //    d'usage, et c'est le GLN qui désigne la base réellement ouverte. La DEVISE s'y ajoute dès
  //    qu'elle n'est plus l'évidence (≠ EUR) : tue, elle laisserait lire des montants sans monnaie.
  const devise = view.active && view.active.currency.code !== 'EUR' ? ` · ${view.active.currency.code}` : '';
  // ⚠️ LE CODE MAGASIN SUIT LE GLN (maquette : « GLN 3760124500017 · S012 » — corrigé 2026-09-05). Il
  //    manquait sur la capsule FERMÉE alors que la liste déroulante l'affichait déjà : le même
  //    magasin se nommait donc de deux façons selon qu'on avait ouvert le menu ou non.
  // ⚠️ IL VIENT DE LA LISTE DÉJÀ CHARGÉE, pas d'un nouveau champ serveur : `stores` porte `sCode`
  //    depuis toujours. Ajouter la même donnée à `active` en aurait fait deux sources pour un même
  //    fait, et un jour l'une des deux aurait eu tort.
  // ⚠️ ABSENT S'IL N'EST PAS CONNU (cross-tenant hors registre) : pas de séparateur orphelin.
  const code = view.active ? view.stores.find((x) => x.gln === view.active!.gln)?.sCode ?? null : null;
  // ⚠️ « GLN {gln} » EST LA PHRASE ; le code magasin et la devise sont des SUFFIXES, pas des mots.
  //    Les faire entrer dans la clé (`GLN {gln}{v}{devise}`) aurait livré au traducteur une clé qui
  //    n'est que des trous collés — rien à traduire, et un ordre qu'il ne peut pas changer.
  const meta = view.active
    ? `${t('GLN {gln}', { gln: view.active.gln })}${code ? ` · ${code}` : ''}${devise}`
    : t('{n} magasin ouvert à ce compte', { n: view.stores.length });
  // ⚠️ « UN SEUL MAGASIN » NE DÉSACTIVE QUE S'IL EST DÉJÀ OUVERT : sans magasin actif, le chevron est la
  //    seule sortie de l'écran « aucun magasin actif », et le désactiver laissait la session sans base.
  const motifDesactive = !view.selectable
    ? t('Session bornée à ce magasin.')
    : view.stores.length <= 1 && view.active ? t('Un seul magasin est ouvert à ce compte.') : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
      <CapsuleMagasin
        nom={view.active?.name ?? t('Choisir un magasin…')}
        meta={meta}
        stores={view.stores}
        activeGln={view.active?.gln ?? null}
        onChoose={choose}
        motifDesactive={motifDesactive}
        estampe={!view.active}
      />
      {!view.active && (
        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.45, minWidth: 0 }}>
          
          {t('Choisissez le magasin à administrer : les écrans lisent sa base, aucun n’affiche plusieurs magasins.')}
        </span>
      )}
    </div>
  );
}

/** L'icône bâtiment de la maquette — la même sur la capsule et les rangées du menu. */
function IconeMagasin({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="4" y="3" width="16" height="18" rx="1" /><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h6M9 19h6" />
    </svg>
  );
}

/**
 * LA CAPSULE MAGASIN de la barre du haut — portée PIXEL PERFECT de la maquette (`data-tour="store"`),
 * et son menu « Changer de magasin ».
 *
 * ⚠️ LE MÊME DESSIN DANS TOUS LES CAS (décision utilisateur 2026-08-29) : mono-magasin, multi et
 * cross-tenant portent la même capsule à chevron — en mono le chevron est DÉSACTIVÉ avec son motif
 * en infobulle, jamais absent : l'œil retrouve le même repère sur tous les comptes.
 * ⚠️ ELLE CHANGE DE BASE, ELLE NE FILTRE PAS : le choix confirme, écrit au serveur puis RECHARGE la
 * page (le `choose` du contexte) — c'est la doctrine « aucune vue ne mélange deux magasins ».
 */
function CapsuleMagasin({ nom, meta, stores, activeGln, onChoose, motifDesactive, estampe }: {
  nom: string;
  meta: string;
  stores: StoreContextView['stores'];
  activeGln: string | null;
  onChoose: (gln: string) => Promise<void>;
  /** Non nul = le chevron est rendu DÉSACTIVÉ, avec ce motif en infobulle. */
  motifDesactive: string | null;
  /** Aucun magasin encore chargé : le nom s'affiche en retrait (c'est une invite, pas un état). */
  estampe?: boolean;
}) {
  const [ouvert, setOuvert] = useState(false);
  const actif = motifDesactive == null;

  const declencheur = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);

  // ⚠️ Échap ferme le menu — un panneau qu'on ne peut quitter qu'à la souris se referme mal au clavier — et le focus
  //    revient au déclencheur : laissé sur un élément démonté, il repartirait du haut de la page.
  useEffect(() => {
    if (!ouvert) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOuvert(false); declencheur.current?.focus(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ouvert]);

  // ⚠️ UN MENU S'OUVRE SUR SON PREMIER ÉLÉMENT : ses éléments sont hors de la tabulation, les flèches les parcourent.
  useEffect(() => {
    if (ouvert) menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [ouvert]);

  return (
    <div style={{ position: 'relative', minWidth: 0 }}>
      {/* La capsule relevée : icône bâtiment, nom + méta sur deux lignes, chevron. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, height: 38, padding: '0 12px',
        borderRadius: 'var(--r-ctrl)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
        flexShrink: 0, maxWidth: 300, minWidth: 0,
      }}>
        <span style={{ display: 'flex', color: 'var(--ink-500)', flexShrink: 0 }}><IconeMagasin /></span>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: estampe ? 'var(--ink-500)' : 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nom}</span>
          <span className="mono" style={{ fontSize: '10px', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{meta}</span>
        </div>
        <button
          ref={declencheur}
          type="button"
          aria-label={t('Changer de magasin')}
          aria-expanded={ouvert || undefined}
          aria-disabled={!actif || undefined}
          title={actif ? t('Changer de magasin') : motifDesactive}
          onClick={() => { if (actif) setOuvert((o) => !o); }}
          style={{
            width: 26, height: 26, marginLeft: 2, border: 'none', borderRadius: 'var(--r-xs)',
            background: 'var(--paper)', color: 'var(--ink-500)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0,
            boxShadow: '0 1px 2px rgba(15,20,25,.06)',
            opacity: actif ? 1 : 0.45, cursor: actif ? 'pointer' : 'not-allowed',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 9l6 6 6-6" /></svg>
        </button>
      </div>

      {ouvert && (
        <>
          <div onClick={() => setOuvert(false)} style={{ position: 'fixed', inset: 0, zIndex: 520 }} aria-hidden />
          {/* Le panneau relevé (maquette `storeMenuOpen`) : titre, rangées à point d'état, note de bas. */}
          <div ref={menu} role="menu" aria-label={t('Changer de magasin')} style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, width: 392, maxWidth: 'calc(100vw - 40px)', zIndex: 521,
            background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
            boxShadow: '0 22px 60px -12px rgba(12,14,16,.35)', overflow: 'hidden',
          }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--ink-200)' }}>
              <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Changer de magasin')}</div>
              <div style={{ fontSize: '12.5px', color: 'var(--ink-600)', marginTop: 2 }}>
                
                {t('Chaque magasin a sa propre base : le back-office se recharge entièrement sur le magasin choisi.')}
              </div>
            </div>
            <div style={{ padding: 8, maxHeight: 340, overflowY: 'auto' }}>
              {stores.map((s, i) => {
                const current = s.gln === activeGln;
                return (
                  <button
                    key={s.gln} type="button" role="menuitem"
                    {...rovingMenuItem({ index: i, count: stores.length })}
                    onClick={() => { setOuvert(false); if (!current) void onChoose(s.gln); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '11px 12px',
                      border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', textAlign: 'left', cursor: 'pointer',
                    }}
                  >
                    <span style={{ width: 9, height: 9, borderRadius: 'var(--r-pill)', background: current ? 'var(--teal-500)' : 'var(--ink-300)', flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
                      <span className="mono" style={{ display: 'block', fontSize: '10.5px', color: 'var(--ink-400)' }}>GLN {s.gln}{s.sCode ? ` · ${s.sCode}` : ''}</span>
                    </span>
                    {current ? (
                      <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--teal-800)', whiteSpace: 'nowrap' }}>{t('En cours')}</span>
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--ink-400)', flexShrink: 0 }} aria-hidden><path d="M9 6l6 6-6 6" /></svg>
                    )}
                  </button>
                );
              })}
            </div>
            <div style={{ padding: '12px 16px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--ink-400)', display: 'flex', flexShrink: 0, marginTop: 1 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8v.5" /></svg>
              </span>
              <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.5 }}>
                
                {t('Aucune vue ne mélange deux magasins. Pour comparer, c’est Orgabusiness.')}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Une ligne de bandeau : une étiquette courte qui se lit d'un coup d'œil, puis la phrase qui explique. */
function Row({ tone, etiquette, texte }: { tone: 'neutre' | 'alerte'; etiquette: string; texte: string }) {
  const alerte = tone === 'alerte';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <span
        style={{
          fontSize: 'var(--t-eyebrow)', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
          color: alerte ? 'var(--amber-fg)' : 'var(--ink-500)',
          background: alerte ? 'var(--amber-bg)' : 'transparent',
          border: alerte ? '1px solid transparent' : '1px solid var(--ink-200)',
          padding: '3px 8px', borderRadius: 'var(--r-ctrl)', whiteSpace: 'nowrap',
        }}
      >
        {etiquette}
      </span>
      <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.45, minWidth: 0 }}>
        {texte}
      </span>
    </div>
  );
}
